import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import CheckBox from '../../shared/TailwindComponents/CheckBox';
import Input from '../../shared/TailwindComponents/Input';
import FieldShell from '../../shared/TailwindComponents/FieldShell';
import apis from '../../../utils/apis';
import { formatRoleNameForDisplay, formatRoleNameForBackend } from '../../../utils/roleFormatter';
import * as PiIcons from 'react-icons/pi';
import { PiGearLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { readErrorMessage } from '../../../utils/errorMessage';

interface Role {
  id: string | number;
  name: string;
  description?: string;
  display_name?: string;
  permission_ids: number[];
  permission_count: number;
  is_active: boolean;
  is_custom: boolean;
  is_default: boolean;
  created_at: string;
  updated_at?: string;
  org_type?: string;
}

interface Permission {
  id: number;
  name: string;
  section_id: number;
  description?: string;
}

interface PermissionSection {
  id: number;
  name: string;
  category?: string;
  description?: string;
  permissions: Permission[];
  permission_count: number;
}

interface AdminRoleModalProps {
  mode: 'create' | 'edit';
  role?: Role;
  onConfirm: (roleData: any) => void;
  onCancel: () => void;
  orgType?: string;
}

const sectionIconMap: Record<string, string> = {
  'Dashboard': 'PiDashboard',
  'Tasks': 'PiCheckSquare',
  'Calendar': 'PiCalendar',
  'Messages': 'PiChat',
  'Clients': 'PiUsers',
  'Lawyers': 'PiUserCircle',
  'Projects': 'PiTreeStructure',
  'Matters': 'PiBriefcase',
  'Files': 'PiFiles',
  'Research': 'PiMagnifyingGlass',
  'Tables': 'PiGridFour',
  'Analytics': 'PiChartBar',
  'Data Sources': 'PiDatabase',
  'Finder': 'PiUserSwitch',
  'Templates': 'PiFileText',
  'People': 'PiUsers',
  'Departments': 'PiBuilding',
  'Teams': 'PiUsersThree',
  'Finance': 'PiCoinVertical',
};

const getIconByName = (name: string): JSX.Element => {
  const normalizedName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  const iconKey = sectionIconMap[normalizedName] || sectionIconMap[name] || 'PiGear';
  const IconComponent = (PiIcons as any)[iconKey + 'Light'] || (PiIcons as any)[iconKey];
  return IconComponent ? <IconComponent className="h-4 w-4" /> : <PiGearLight className="h-4 w-4" />;
};

const AdminRoleModal: React.FC<AdminRoleModalProps> = ({
  mode,
  role,
  onConfirm,
  onCancel,
  orgType = 'law_firm',
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);
  const [permissions, setPermissions] = useState<PermissionSection[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  useEffect(() => {
    if (mode === 'edit' && role) {
      setRoleName(role.display_name || role.name);
      setRoleDescription(role.description || '');
      setSelectedPermissionIds(role.permission_ids);
    }
    fetchPermissions();
  }, [mode, role]);

  const fetchPermissions = useCallback(async () => {
    try {
      setIsLoadingPermissions(true);
      const res = await apis.get('/admin/permissions', {
        params: {
          org_type: orgType
        }
      });
      if (res && res.data && res.data.data) {
        setPermissions(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
    } finally {
      setIsLoadingPermissions(false);
    }
  }, [orgType]);

  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSectionToggle = (sectionId: number) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleSubmit = async () => {
    const trimmed = roleName.trim();
    if (!trimmed) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Role name is required'),
        modalType: 'warning',
        duration: 3000
      }));
      return;
    }
    if (trimmed.length < 2) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Role name must be at least 2 characters'),
        modalType: 'warning',
        duration: 3000
      }));
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        display_name: trimmed,
        description: roleDescription,
        permission_ids: selectedPermissionIds,
      };

      if (mode === 'create') {
        await apis({
          method: 'POST',
          url: '/admin/roles',
          data: payload,
        });
      } else if (mode === 'edit' && role) {
        await apis({
          method: 'PUT',
          url: `/admin/roles/${role.id}`,
          data: payload,
        });
      }

      onConfirm(payload);
    } catch (err: any) {
      console.error('Error saving role:', err);
      const errorMsg = readErrorMessage(err, t('Failed to save role'));
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMsg,
        modalType: 'error',
        duration: 4000
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const PermissionSelector = () => (
    <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700">
      {permissions.map((section) => (
        <div key={section.id}>
          <button
            onClick={() => handleSectionToggle(section.id)}
            className="w-full flex items-center justify-between p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          >
            <div className="flex items-center gap-2">
              {getIconByName(section.name)}
              <span className="text-sm font-medium text-gray-900 dark:text-white">{section.name}</span>
            </div>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections[section.id] ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {expandedSections[section.id] && (
            <div className="ml-4 space-y-2 border-l-2 border-gray-300 dark:border-gray-600 pl-3">
              {section.permissions.map((permission) => (
                <CheckBox
                  key={permission.id}
                  label={permission.name}
                  translate={false}
                  checked={selectedPermissionIds.includes(permission.id)}
                  onChange={() => handlePermissionToggle(permission.id)}
                  disabled={isSaving}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-w-[700px] sm:w-full max-h-[80vh] flex flex-col">
      <div className="flex-1 overflow-y-auto space-y-3 mb-4 px-6 py-4">
        <div className="flex gap-3">
          <div className="flex-1 w-2/5">
            <Input
              label={t('Role Name')}
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder={t('e.g., Senior Attorney')}
              disabled={isSaving || mode === 'edit'}
              description={
                mode === 'edit'
                  ? t('Role name cannot be changed')
                  : t('Must be 2-100 characters')
              }
            />
          </div>

          <div className="flex-1 w-3/5">
            <Input
              label={t('Description')}
              type="text"
              value={roleDescription}
              onChange={(e) => setRoleDescription(e.target.value)}
              placeholder={t('Brief description of the role')}
              disabled={isSaving}
            />
          </div>
        </div>

        <FieldShell
          label={`${t('Assign Permissions')} (${selectedPermissionIds.length})`}
          translate={false}
        >
          {isLoadingPermissions ? (
            <div className="text-center py-8 text-fg-muted">{t('Loading permissions...')}</div>
          ) : (
            <PermissionSelector />
          )}
        </FieldShell>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex gap-3 justify-end px-6 py-4">
        <Button
          variant="neutral"
          size="small"
          onClick={onCancel}
          disabled={isSaving}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={isSaving || !roleName.trim()}
        >
          {isSaving ? t('Saving...') : (mode === 'create' ? t('Create Role') : t('Update Role'))}
        </Button>
      </div>
    </div>
  );
};

export default AdminRoleModal;
